import { IconButton } from '@material-ui/core';
import * as React from 'react';
import { useMemo } from 'react';
import { createDomicilioText } from 'shared/models/DomicilioGoogle';
import { TransferReservationTableTimesCell } from '../../viajeTraslado/components/TransferReservationTableTimesCell';
import { TransferReservationManagementListModel } from '../models/TransferReservationManagementList';
import InfoIcon from '@material-ui/icons/Info';
import { ViajeTrasladosGestionEstado } from '../../viajeTraslado/models/TransferReservationPartial';
import { copyReservationPublicNewLinkToClipboard } from '../../viajeTraslado/services/copyReservationPublicLinkToClipboard';
import ServiceType from '../../coordinar/components/list/serviceType';
import Client from '../../coordinar/components/list/client';
import PassengersItem from 'app/viaje/components/shared/passengers';
import Driver from '../../coordinar/components/list/driver';
import { useResponsiveBreakpoint } from '@vadiun/react-hooks';

import { GestionColumnVisibility } from '../utils/gestionStorage';
import Points from '../../coordinar/components/list/points';
import ExpandableTextCell from 'shared/components/Table/ExpandableTextCell';
import { NewTable, NewTableColumn } from 'shared/components/NewTable';
import ActionsItemGestion from './list/ActionsItemGestion';
import { Chip } from '@vadiun/react-components';

interface TableQueryData {
  data: TransferReservationManagementListModel[];
  total: number;
  isLoading: boolean;
  error: Error | undefined;
  serverSideOptions: any;
}
interface Props {
  tableQuery: TableQueryData;
  onLocationsDetails: (
    reservation: TransferReservationManagementListModel
  ) => void;
  onReservationCopy: (
    reservation: TransferReservationManagementListModel
  ) => void;
  onReservationShowDetails: (id: number) => React.ReactNode;
  setCanceledTravelInfo: React.Dispatch<
    React.SetStateAction<ViajeTrasladosGestionEstado | undefined>
  >;
  onOpenEditLLtPais: (
    reservation: TransferReservationManagementListModel
  ) => void;
  onBusesPreAssign: (travel: TransferReservationManagementListModel) => void;
  handlePageChange: (currentPage: number) => void;
  onShowDriverDetails: (reservationId: number, driverId?: number) => void;
  columnVisibility?: GestionColumnVisibility;
  onChangePage: (newPage: number) => void;
  onChangeRowsPerPage: (newRowsPerPage: number) => void;
  onSortChange: (direction: string | null, sortBy: string) => void;
}

export function ViajeTrasladoGestionTable({
  tableQuery,
  onLocationsDetails,
  onReservationCopy,
  onReservationShowDetails,
  setCanceledTravelInfo,
  onOpenEditLLtPais,
  onBusesPreAssign,
  handlePageChange,
  onShowDriverDetails,
  columnVisibility,
  onChangePage,
  onChangeRowsPerPage
}: Props) {
  const display = useResponsiveBreakpoint();
  const isMobileOrTablet =
    display === 'xs' || display === 'sm' || display === 'md';

  const data = useMemo(
    () =>
      (tableQuery.data ?? []).map((reservation) => {
        const passengers = reservation.locations
          .map((location) =>
            location.passengers.map((passenger) => passenger.name)
          )
          .flat();
        return {
          ...reservation,
          schedule: {
            arrival: reservation.reservationDatetime,
            exit: reservation.reservationDatetime
              .clone()
              .add(-reservation.advanceHours, 'hours'),
            end: reservation.arrivalDateTime
          },
          business_unit: reservation.assigned_driver?.businessUnit?.name || '-',
          driver_name: reservation.assigned_driver
            ? `${reservation.assigned_driver?.nombreCompleto} - ${reservation.assigned_driver?.codigo} `
            : '-',
          driver_price:
            reservation.final_prices?.driver_price ||
            reservation.calculated_prices?.driver_price ||
            reservation.estimated_prices?.driver_price,
          passengers,
          passengers_name: passengers.toString(),
          client_name: reservation.client.name,
          addresses: reservation.locations
            .map(
              (location, index) =>
                `${index + 1} - ${createDomicilioText(location.address)}`
            )
            .flat()
            .toString(),
          reservation_datetime:
            reservation.reservationDatetime.format('DD-MM-YY HH:mm')
        };
      }),
    [tableQuery.data]
  );

  const columns = useMemo<NewTableColumn<any>[]>(
    () => [
      {
        key: 'schedule',
        label: 'Horarios',
        align: 'center',
        width: '120px',
        render: (row) => {
          const schedule = row?.schedule;
          return <TransferReservationTableTimesCell schedule={schedule} />;
        }
      },
      {
        key: 'client_passengers',
        label: 'Cliente y Pasajeros',
        width: '180px',
        align: 'center',
        render: (row) => (
          <div className="flex flex-col gap-4 justify-center">
            <div className="text-sm font-medium">
              <Client current={row} isMobileOrTablet={false} />
            </div>
            <div className="text-xs text-gray-500 dark:text-inkMuted-dark">
              <PassengersItem passengers={row.passengers} idTravel={row.id} />
            </div>
          </div>
        )
      },
      {
        key: 'puntos',
        label: 'Domicilios',
        width: '280px',
        render: (row, index) => {
          return (
            <Points
              data={data}
              index={index}
              isMobileOrTablet={isMobileOrTablet}
              onLocationsDetails={onLocationsDetails}
            />
          );
        }
      },
      {
        key: 'group_observations',
        label: 'Observaciones',
        width: '180px',
        align: 'center',
        render: (row) => (
          <div className="flex flex-col gap-4 text-xs w-36">
            {row.coordinationObservation && (
              <div className="flex flex-col items-start text-gray-600 dark:text-gray-300">
                <span className="font-semibold text-gray-700 dark:text-gray-200">
                  Coord:{' '}
                </span>
                <ExpandableTextCell
                  text={row.coordinationObservation}
                  maxWidthClass="w-full"
                  isMobileOrTablet={isMobileOrTablet}
                />
              </div>
            )}
            {row.general_observation && (
              <div className="flex flex-col items-start text-gray-600 dark:text-gray-300">
                <span className="font-semibold text-gray-700 dark:text-gray-200">
                  Chofer:{' '}
                </span>
                <ExpandableTextCell
                  text={row.general_observation}
                  maxWidthClass="w-full"
                  isMobileOrTablet={isMobileOrTablet}
                />
              </div>
            )}
            {row.lltpais?.observation_lltpais && (
              <div className="flex flex-col items-start text-gray-600 dark:text-gray-300">
                <span className="font-semibold text-gray-700 dark:text-gray-200">
                  LLT-País:{' '}
                </span>
                <ExpandableTextCell
                  text={row.lltpais?.observation_lltpais}
                  maxWidthClass="w-full"
                  isMobileOrTablet={isMobileOrTablet}
                />
              </div>
            )}
          </div>
        )
      },
      {
        key: 'serviceType',
        label: 'Tipo de servicio',
        align: 'center',
        width: '180px',
        render: (row, index) => (
          <ServiceType data={data} index={index} isMobileOrTablet={false} />
        )
      },
      {
        key: 'preAssignedDriver',
        label: isMobileOrTablet ? ' ' : 'Chofer Pre-asignado',
        align: 'center',
        width: '180px',
        render: (row, index) => (
          <Driver
            data={data}
            index={index}
            isMobileOrTablet={isMobileOrTablet}
            onShowDriverDetails={(driver) =>
              onShowDriverDetails(row.id, driver?.id)
            }
          />
        )
      },
      {
        key: 'assigned_driver',
        label: 'Chofer Asignado',
        align: 'center',
        width: '180px',
        render: (row, index) => (
          <div className="flex flex-col gap-2 ">
            <div className="flex ">
              {data[index].assigned_driver && (
                <p
                  className="cursor-pointer rounded-md bg-gray-50 p-2 dark:bg-gray-900/40 w-fit text-blue-600 dark:text-blue-400"
                  onClick={() =>
                    onShowDriverDetails(row.id, row.assigned_driver?.id)
                  }
                >
                  {data[index].assigned_driver?.codigo} -{' '}
                  {data[index].assigned_driver?.nombreCompleto}
                </p>
              )}
            </div>
            {data[index].serviceType === 'Buses' &&
              data[index].assigned_driver &&
              data[index].bus_requirement?.vehicle?.patent && (
                <p className="rounded bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-800 dark:bg-blue-950/40 dark:text-blue-300">
                  {data[index].bus_requirement?.vehicle?.patent}
                </p>
              )}
            {row.business_unit && (
              <span className="text-gray-700 dark:text-ink-dark">
                {row.business_unit}
              </span>
            )}
          </div>
        )
      },
      {
        key: 'driver_price',
        label: 'Precio del chofer',
        align: 'center',
        width: '160px',
        render: (row) => (
          <span className="text-gray-700 dark:text-ink-dark">
            {row.driver_price}
          </span>
        )
      },
      {
        key: 'group_additional',
        label: 'Información Adicional',
        width: { '2xl': '160px' },
        align: 'center',
        render: (row) => (
          <div className="flex flex-col gap-4 text-xs w-36">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-ink dark:text-ink-dark text-sm">
                {row.id}
              </span>
            </div>
            {row.platformId && (
              <div className="flex flex-col items-start text-gray-600 dark:text-gray-300">
                <span className="font-semibold text-gray-400">Plataforma:</span>
                <ExpandableTextCell
                  text={row.platformId}
                  maxWidthClass="w-full"
                  isMobileOrTablet={isMobileOrTablet}
                />
              </div>
            )}
            {row.event_name && (
              <div className="flex flex-col items-start text-gray-600 dark:text-gray-300">
                <span className="font-semibold text-gray-400">Evento:</span>
                <ExpandableTextCell
                  text={row.event_name}
                  maxWidthClass="w-full"
                  isMobileOrTablet={isMobileOrTablet}
                />
              </div>
            )}
          </div>
        )
      },
      {
        key: 'state',
        label: 'Estado',
        align: 'center',
        width: '160px',
        render: (row, index) => {
          const currentTravel = data[index];
          const currentState = currentTravel.state;
          const canceledTravelInfo = currentTravel.cancellation_state;
          return (
            <div className="flex gap-3 items-center justify-center">
              <Chip variant="light" className="whitespace-nowrap">
                {currentState}{' '}
              </Chip>
              {canceledTravelInfo?.datetime ? (
                <IconButton
                  onClick={() =>
                    setCanceledTravelInfo({
                      ...canceledTravelInfo,
                      observation: canceledTravelInfo.observation ?? '',
                      user: canceledTravelInfo.user ?? {
                        id: 0,
                        name: 'Usuario no disponible',
                        email: '',
                        isDeleted: true
                      }
                    })
                  }
                  aria-label="Refresh"
                  size="small"
                >
                  <InfoIcon className="text-gray-300" />
                </IconButton>
              ) : null}
            </div>
          );
        }
      },
      {
        key: 'actions',
        label: 'Acciones',
        align: 'center',
        width: '200px',
        render: (row) => (
          <ActionsItemGestion
            currentTravel={row}
            onBusesPreAssign={onBusesPreAssign}
            onReservationCopy={onReservationCopy}
            onReservationShowDetails={onReservationShowDetails}
            onOpenEditLLtPais={onOpenEditLLtPais}
            copyReservationPublicNewLinkToClipboard={
              copyReservationPublicNewLinkToClipboard
            }
          />
        )
      }
    ],
    [
      data,
      isMobileOrTablet,
      onShowDriverDetails,
      onLocationsDetails,
      onReservationCopy,
      onBusesPreAssign,
      onOpenEditLLtPais,
      onReservationShowDetails,
      setCanceledTravelInfo
    ]
  );

  const currentPage = (tableQuery.serverSideOptions?.page ?? 0) + 1;
  const currentRowsPerPage = tableQuery.serverSideOptions?.rowsPerPage ?? 50;
  const parsedColumnVisibility = useMemo(() => {
    if (!columnVisibility) return undefined;
    return Object.keys(columnVisibility).reduce((acc, key) => {
      acc[key] = Boolean((columnVisibility as any)[key]);
      return acc;
    }, {} as Record<string, boolean>);
  }, [columnVisibility]);
  return (
    <NewTable<any>
      columns={columns}
      columnVisibility={parsedColumnVisibility}
      data={data}
      getRowKey={(event) => event.id}
      isLoading={tableQuery.isLoading}
      emptyTitle="No hay viajes finalizados para mostrar"
      page={currentPage}
      total={tableQuery.total}
      rowsPerPage={currentRowsPerPage}
      onChangePage={(newPage) => {
        onChangePage(newPage);
        handlePageChange(newPage);
      }}
      onChangeRowsPerPage={onChangeRowsPerPage}
      rowsPerPageOptions={[10, 30, 50, 100, 200, 300, 400, 500]}
      stickyHeader={{ top: '44px', zIndex: 1 }}
    />
  );
}
